import { DeliveryType } from 'hx-services';
import { ClientKind, DiscountToggle } from '@callcenter-app/service/order-editor.service';
import { AddressDetails, DETAIL_LIMIT, NOTE_LIMIT, REFERRER_LIMIT, TEXT_LIMIT } from './booked-draft';
import { cleanCompanyDraft, CompanyDraft } from './company';
import { EditAddress, EditChanges, EditPartyRef, EditSlot } from './edit-form';
import { DRAFT_TTL_MS, draftKey } from './order-draft';
import { cleanRejectionDraft, RejectionDraft } from './rejections';

/**
 * The draft of the change of an order in work (design §5.3): what the operator changed in the form and what the flows of the page hold besides — the requisites typed and not saved, the refusals of
 * the conversation. The order itself (the lines, the time, the address, the client, the files) is the server's: every opening reads it (E12), and the draft is laid over it. The key is
 * `cc.orderEditor.<userId>.edit.<orderId>`: one operator, one order, one tab (`sessionStorage`), 30 minutes since the last touch — the rules of the other drafts.
 *
 * `orderVersion` is the version of the order the changes were made over: a draft that was made over another version is shown as «ваши несохранённые правки» with a warning, not silently. The names
 * of the people are NOT kept (only the id and the number): the storage of a tab holds no stranger's name. Pure; never put in `localStorage` or in the address of the page.
 */

export const EDIT_DRAFT_VERSION = 1;

export interface EditDraft {
  version: typeof EDIT_DRAFT_VERSION;
  orderId: number;
  userId: number;
  /** Browser clock, ms: only the 30-minute term is measured with it. */
  touchedAt: number;
  /** The `version` of the order (E12) the changes were made over. */
  orderVersion: number;
  changes: EditChanges;
  /** The forms of the legal entity as they are typed: the requisites, the letter of authority. Absent — nothing typed. */
  company?: CompanyDraft;
  /** The refusals of the conversation (E24). */
  rejection?: RejectionDraft;
}

export function newEditDraft(orderId: number, userId: number, orderVersion: number, nowMs: number): EditDraft {
  return { version: EDIT_DRAFT_VERSION, orderId, userId, touchedAt: nowMs, orderVersion, changes: {} };
}

export function editTouched(draft: EditDraft, nowMs: number): EditDraft {
  return { ...draft, touchedAt: nowMs };
}

export function isEditExpired(draft: Pick<EditDraft, 'touchedAt'>, nowMs: number): boolean {
  return nowMs - draft.touchedAt >= DRAFT_TTL_MS;
}

/** `cc.orderEditor.<userId>.edit.<orderId>`. */
export function editKey(userId: number, orderId: number): string {
  return draftKey(userId, 'edit', String(orderId));
}

export function serializeEditDraft(draft: EditDraft): string {
  return JSON.stringify(draft);
}

// ---------- reading: every piece of a stored draft is checked, it is never trusted as it is ----------

type Dict = Record<string, unknown>;

function isDict(value: unknown): value is Dict {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown, max: number): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

function optionalStr(value: unknown, max: number): string | undefined {
  return typeof value === 'string' ? value.slice(0, max) : undefined;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function whole(value: unknown): number | undefined {
  const n = num(value);
  return n !== undefined && Number.isInteger(n) ? n : undefined;
}

const TIME_RE = /^\d{2}:\d{2}$/;

function timeOf(value: unknown): string | undefined {
  return typeof value === 'string' && TIME_RE.test(value) ? value : undefined;
}

function typeOf(value: unknown): DeliveryType | undefined {
  return value === 'PICKUP' || value === 'DELIVERY' ? value : undefined;
}

function addressOf(value: unknown): EditAddress | undefined {
  if (!isDict(value) || typeof value['address'] !== 'string') {
    return undefined;
  }
  const admDiv = optionalStr(value['admDiv'], 200);
  const alias = optionalStr(value['alias'], 200);
  const lat = num(value['lat']);
  const lng = num(value['lng']);
  return {
    address: value['address'].slice(0, 200),
    ...(admDiv ? { admDiv } : {}),
    ...(lat !== undefined && lng !== undefined ? { lat, lng } : {}),
    ...(alias ? { alias } : {}),
  };
}

function detailsOf(value: unknown): AddressDetails | undefined {
  return isDict(value)
    ? { entrance: str(value['entrance'], DETAIL_LIMIT), floor: str(value['floor'], DETAIL_LIMIT), flat: str(value['flat'], DETAIL_LIMIT), note: str(value['note'], NOTE_LIMIT) }
    : undefined;
}

function slotOf(value: unknown): EditSlot | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const rangeId = whole(value['rangeId']);
  const from = timeOf(value['from']);
  const to = timeOf(value['to']);
  return rangeId !== undefined && from && to ? { rangeId, from, to } : undefined;
}

function partyOf(value: unknown): EditPartyRef | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const phone = typeof value['phone'] === 'string' ? value['phone'].replace(/\D/g, '').slice(0, 15) : '';
  return phone ? { phone } : undefined;
}

function togglesOf(value: unknown): DiscountToggle[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const out: DiscountToggle[] = [];
  value.forEach(item => {
    const promoId = isDict(item) ? whole(item['promoId']) : undefined;
    if (promoId !== undefined && isDict(item)) {
      const priceId = whole(item['priceId']);
      out.push({ promoId, ...(priceId !== undefined ? { priceId } : {}), enabled: false });
    }
  });
  return out;
}

function removedOf(value: unknown): Record<string, number> | undefined {
  if (!isDict(value)) {
    return undefined;
  }
  const out: Record<string, number> = {};
  Object.entries(value).forEach(([key, count]) => {
    const n = whole(count);
    if (/^\d{1,12}$/.test(key) && n !== undefined && n > 0 && n <= 999) {
      out[key] = n;
    }
  });
  return Object.keys(out).length > 0 ? out : undefined;
}

/** The changes of a stored draft: every key checked, what is not what it should be is dropped (the form then follows the order). */
export function cleanEditChanges(raw: unknown): EditChanges {
  if (!isDict(raw)) {
    return {};
  }
  const type = typeOf(raw['type']);
  const pickupTime = timeOf(raw['pickupTime']);
  const address = addressOf(raw['address']);
  const details = detailsOf(raw['details']);
  const slot = slotOf(raw['slot']);
  const manualPriceId = whole(raw['manualPriceId']);
  const removed = removedOf(raw['removed']);
  const client = partyOf(raw['client']);
  const recipient = raw['recipient'] === null ? null : partyOf(raw['recipient']);
  const clientType: ClientKind | undefined = raw['clientType'] === 'LEGAL' ? 'LEGAL' : raw['clientType'] === 'INDIVIDUAL' ? 'INDIVIDUAL' : undefined;
  const decorText = optionalStr(raw['decorText'], TEXT_LIMIT);
  const filesRemoved = Array.isArray(raw['filesRemoved'])
    ? raw['filesRemoved'].map(whole).filter((n): n is number => n !== undefined && n > 0).slice(0, 50)
    : undefined;
  const note = optionalStr(raw['note'], TEXT_LIMIT);
  const comment = optionalStr(raw['comment'], TEXT_LIMIT);
  const referrer = raw['referrer'] === null ? null : optionalStr(raw['referrer'], REFERRER_LIMIT);
  const disabledDiscounts = togglesOf(raw['disabledDiscounts']);
  return {
    ...(type ? { type } : {}),
    ...(pickupTime ? { pickupTime } : {}),
    ...(address ? { address } : {}),
    ...(details ? { details } : {}),
    ...(slot ? { slot } : {}),
    ...(manualPriceId !== undefined ? { manualPriceId } : {}),
    ...(removed ? { removed } : {}),
    ...(client ? { client } : {}),
    ...(recipient !== undefined ? { recipient } : {}),
    ...(clientType ? { clientType } : {}),
    ...(decorText !== undefined ? { decorText } : {}),
    ...(filesRemoved && filesRemoved.length > 0 ? { filesRemoved } : {}),
    ...(note !== undefined ? { note } : {}),
    ...(comment !== undefined ? { comment } : {}),
    ...(referrer !== undefined ? { referrer } : {}),
    ...(typeof raw['attachReceipt'] === 'boolean' ? { attachReceipt: raw['attachReceipt'] } : {}),
    ...(typeof raw['debt'] === 'boolean' ? { debt: raw['debt'] } : {}),
    ...(disabledDiscounts ? { disabledDiscounts } : {}),
  };
}

export function parseEditDraft(json: string | null | undefined): EditDraft | undefined {
  if (!json) {
    return undefined;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return undefined;
  }
  if (!isDict(raw) || raw['version'] !== EDIT_DRAFT_VERSION) {
    return undefined;
  }
  const orderId = whole(raw['orderId']);
  const userId = whole(raw['userId']);
  const touchedAt = num(raw['touchedAt']);
  const orderVersion = whole(raw['orderVersion']);
  if (orderId === undefined || userId === undefined || touchedAt === undefined || orderVersion === undefined) {
    return undefined;
  }
  const company = isDict(raw['company']) ? cleanCompanyDraft(raw['company']) : undefined;
  const rejection = cleanRejectionDraft(raw['rejection']);
  return {
    version: EDIT_DRAFT_VERSION,
    orderId,
    userId,
    touchedAt,
    orderVersion,
    changes: cleanEditChanges(raw['changes']),
    ...(company ? { company } : {}),
    ...(rejection ? { rejection } : {}),
  };
}
